import { getStroke } from "perfect-freehand";

import { formatElapsed } from "./storage.ts";
import { resultDescription, type ResultSummary } from "./result-summary.ts";

export interface ResultGlyph {
  data: string;
  aspect: number;
}

export interface ResultArtwork {
  version: 1;
  paper: string;
  glyphs: Record<string, ResultGlyph[]>;
}

export const RESULT_CARD_SIZE = 1200;

function escapeXml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll('"', "&quot;");
}

function stroke(points: number[][], size: number, colour: string, opacity = 0.9): string {
  const outline = getStroke(points, { size, thinning: 0.5, simulatePressure: false, smoothing: 0.65, streamline: 0.35 });
  const commands = outline.map((point, index) => `${index ? "L" : "M"}${point[0]!.toFixed(2)} ${point[1]!.toFixed(2)}`).join(" ");
  return `<path d="${commands} Z" fill="${colour}" opacity="${opacity}" />`;
}

export function renderResultSvg(summary: ResultSummary, artwork: ResultArtwork, signature?: ResultGlyph): string {
  const seed = Number(summary.date.replaceAll("-", ""));
  const ink = "#233f64";
  const accent = "#b11f4b";
  const glyph = (key: string, position = 0): ResultGlyph => {
    const variants = artwork.glyphs[key];
    const selected = variants?.[(seed + position) % variants.length];
    if (!selected) throw new Error(`Missing result artwork: ${key}`);
    return selected;
  };
  const image = (key: string, centre: number, top: number, height: number, maxWidth: number): string => {
    const selected = glyph(key);
    const fittedHeight = Math.min(height, maxWidth / selected.aspect);
    const width = fittedHeight * selected.aspect;
    return `<image href="${selected.data}" x="${centre - width / 2}" y="${top}" width="${width}" height="${fittedHeight}" />`;
  };
  const numbers = (text: string, centre: number, top: number, height: number, maxWidth: number): string => {
    const letters = [...text].map((character, index) => glyph(character, index));
    const spacing = height * 0.08;
    const naturalWidth = letters.reduce((total, letter) => total + height * letter.aspect, 0) + spacing * (letters.length - 1);
    const scale = Math.min(1, maxWidth / naturalWidth);
    let left = centre - naturalWidth * scale / 2;
    return letters.map((letter) => {
      const width = height * letter.aspect * scale;
      const output = `<image href="${letter.data}" x="${left}" y="${top}" width="${width}" height="${height * scale}" />`;
      left += width + spacing * scale;
      return output;
    }).join("");
  };

  const [year, month, day] = summary.date.split("-");
  const marks = Array.from({ length: summary.maxAttempts }, (_, index) => {
    const centre = 540 + (index - (summary.maxAttempts - 1) / 2) * 100;
    const circle = Array.from({ length: 42 }, (_, point) => {
      const angle = point / 41 * Math.PI * 2;
      return [centre + Math.cos(angle) * (31 + Math.sin(angle * 3 + index)), 856 + Math.sin(angle) * 32, 0.5 + 0.12 * Math.cos(angle)];
    });
    const used = index < summary.attempts;
    const correct = used && summary.solved && index === summary.attempts - 1;
    const outline = stroke(circle, 3.5, ink, used ? 0.65 : 0.24);
    if (!used) return outline;
    return outline + (correct
      ? stroke([[centre - 18, 855, 0.5], [centre - 5, 869, 0.8], [centre + 22, 840, 0.6]], 8, ink)
      : stroke([[centre - 14, 842, 0.6], [centre + 15, 870, 0.75]], 5, accent, 0.74) +
        stroke([[centre + 14, 842, 0.65], [centre - 15, 870, 0.6]], 5, accent, 0.74));
  }).join("");

  const underline = (centre: number, top: number, width: number): string => stroke(
    Array.from({ length: 22 }, (_, index) => [centre - width / 2 + index / 21 * width, top + Math.sin(index / 7) * 2, 0.55 + Math.cos(index / 6) * 0.15]),
    4, ink, 0.5,
  );

  let nameImage = "";
  if (summary.name && signature) {
    const height = Math.min(62, 860 / signature.aspect);
    const width = height * signature.aspect;
    nameImage = `<image data-player-name="" href="${signature.data}" x="${540 - width / 2}" y="${938 - height / 2}" width="${width}" height="${height}" />`;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="1200" viewBox="0 0 1200 1200">
    <title>${escapeXml(resultDescription(summary))}</title>
    <image href="${artwork.paper}" width="1200" height="1200" />
    <g transform="translate(600 600) rotate(-3.5) translate(-540 -540)">
      ${image("title", 540, 86, 166, 935)}
      ${image("puzzle", 248, 283, 42, 195)}
      ${numbers(`#${summary.puzzleNumber}`, 405, 279, 47, 175)}
      ${numbers(`${day}.${month}.${year}`, 754, 283, 40, 370)}
      ${image(summary.solved ? "solved" : "not-solved", 540, 397, 78, 460)}
      ${image("guesses", 296, 535, 53, 265)}
      ${image("time", 776, 535, 53, 240)}
      ${numbers(`${summary.attempts}/${summary.maxAttempts}`, 296, 616, 138, 330)}
      ${numbers(formatElapsed(summary.elapsedMs), 776, 616, 138, 405)}
      ${underline(296, 776, 312)}
      ${underline(776, 776, 375)}
      ${marks}
      ${nameImage}
      ${image("site", 540, 982, 42, 605)}
    </g>
  </svg>`;
}